export const WIZARD_TOTAL_STEPS = 6;

export const formatCurrency = (value) =>
  new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);

export const formatCurrencyDetailed = (value) =>
  new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 2,
  }).format(value || 0);

export const normTr = (s) => (s || "").toString().toLocaleLowerCase("tr");

export const isEnterprisePaket = (paket) => {
  const adi = normTr(paket?.Adi || paket?.PaketAdi);
  return adi.includes("enterprise");
};

export const getPaketYenilemeNotu = (paketAdi) => {
  const adi = normTr(paketAdi);
  if (adi.includes("shift")) {
    return "Paket tutarının tamamı yıllık ödenir.";
  }
  if (adi.includes("fast")) {
    return "Sonraki yıllarda güncel fiyatın %50'si ödenir.";
  }
  if (
    adi.includes("starter") ||
    adi.includes("master") ||
    adi.includes("pro")
  ) {
    return "Sonraki yıllarda güncel fiyatın %20'si ödenir.";
  }
  if (adi.includes("enterprise")) {
    return "Kurumsal çözüm — fiyat için iletişime geçin.";
  }
  return "Yıllık güncelleme ve destek hizmeti dahildir.";
};

export const getPaketOzellikleri = (paket, maxKullanici) => {
  const adi = normTr(paket?.Adi || paket?.PaketAdi);
  const max = maxKullanici || paket?.MaxKullaniciSayisi;

  if (adi.includes("shift")) {
    return [
      { type: "info", text: "1 Kullanıcı Dahil Fiyatıdır." },
      { type: "error", text: "Çoklu Kullanıcı Desteklemez." },
      { type: "error", text: "Ek Modül Satın Alınamaz." },
      { type: "info", text: "Online Banka Entegrasyon Bedeli Yıllık Ödenir." },
      { type: "success", text: "Yıllık Güncelleme & Destek Hizmeti" },
    ];
  }

  if (adi.includes("enterprise")) {
    return [
      { type: "user", text: "Sınırsız Kullanıcı Desteği" },
      {
        type: "success",
        text: "Büyük ölçekli işletmeler için özelleştirilmiş ERP çözümü.",
      },
    ];
  }

  const items = [
    { type: "info", text: "1 Kullanıcı Dahil Fiyatıdır." },
  ];

  if (max && max > 1) {
    items.push({
      type: "user",
      text: `Maksimum ${max} Kullanıcıya Kadar Arttırılabilir.`,
    });
  }

  items.push(
    { type: "success", text: "Ek Modül Satın Alınabilir." },
    { type: "info", text: "Online Banka Entegrasyon Bedeli Yıllık Ödenir." },
    { type: "success", text: "Yıllık Güncelleme & Destek Hizmeti" }
  );

  return items;
};

export const parseModulListesi = (programlarStr) => {
  if (!programlarStr) return [];
  return programlarStr
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
};

export const isOnlineBankaModul = (modul) => {
  const adi = normTr(modul?.ProgramAdi || modul?.ModulAdi);
  return adi.includes("online banka") || adi.includes("onlinebanka");
};

export const isEFatura = (modul) => {
  const ad = normTr(modul?.ProgramAdi || modul?.ModulAdi);
  return ad.includes("e-fatura") || (ad.includes("fatura") && ad.startsWith("e"));
};

export const isEIrsaliye = (modul) => {
  const ad = normTr(modul?.ProgramAdi || modul?.ModulAdi);
  return ad.includes("e-irsaliye") || ad.includes("irsaliye");
};

export const getModulFiyati = (fiyatlar, modulId) =>
  fiyatlar?.Modul?.[modulId] ?? 0;

export const getEFaturaIrsaliyeBirlikteFiyat = (fiyatlar) =>
  Number(fiyatlar?.EFaturaEIrsaliyeBirlikte) || 0;

/**
 * Ek modül kartında gösterilecek fiyat.
 * E-Fatura + E-İrsaliye birlikte alındığında toplam birlikte fiyat uygulanır;
 * biri seçiliyken diğeri kartında tamamlayıcı fiyat gösterilir.
 */
export const getModulGosterimFiyati = (modul, selectedModules, fiyatlar) => {
  if (!modul || modul.isOnlineBanka) return 0;

  const individual = getModulFiyati(fiyatlar, modul.Id);
  if (!isEFatura(modul) && !isEIrsaliye(modul)) {
    return individual;
  }

  const selected = selectedModules || [];
  const selectedEF = selected.find(isEFatura);
  const selectedEI = selected.find(isEIrsaliye);
  const hasEF = Boolean(selectedEF);
  const hasEI = Boolean(selectedEI);
  const birlikte = getEFaturaIrsaliyeBirlikteFiyat(fiyatlar);
  const selfSelected = selected.some((m) => m.Id === modul.Id);

  if (!hasEF && !hasEI) {
    return individual;
  }

  if (hasEF && hasEI) {
    const efIdx = selected.findIndex(isEFatura);
    const eiIdx = selected.findIndex(isEIrsaliye);
    const selfIdx = selected.findIndex((m) => m.Id === modul.Id);
    const firstIdx = Math.min(
      efIdx >= 0 ? efIdx : Infinity,
      eiIdx >= 0 ? eiIdx : Infinity
    );
    const firstMod = selected[firstIdx];
    const firstPrice = getModulFiyati(fiyatlar, firstMod?.Id);
    if (selfIdx === firstIdx) {
      return firstPrice;
    }
    return Math.max(0, birlikte - firstPrice);
  }

  const otherSelected = isEFatura(modul) ? selectedEI : selectedEF;
  if (selfSelected) {
    return individual;
  }
  if (otherSelected) {
    const otherPrice = getModulFiyati(fiyatlar, otherSelected.Id);
    if (birlikte > 0) {
      return Math.max(0, birlikte - otherPrice);
    }
    return individual;
  }

  return individual;
};

export const getOnlineBankaSecenekleri = (fiyatlar) => [
  { value: "1", label: "1 Adet E-Banka", fiyat: fiyatlar?.OnlineBanka1 ?? 0 },
  { value: "5", label: "5 Adet E-Banka", fiyat: fiyatlar?.OnlineBanka5 ?? 0 },
  { value: "10", label: "10 Adet E-Banka", fiyat: fiyatlar?.OnlineBanka10 ?? 0 },
  { value: "15", label: "15 Adet E-Banka", fiyat: fiyatlar?.OnlineBanka15 ?? 0 },
  { value: "20", label: "20 Adet E-Banka", fiyat: fiyatlar?.OnlineBanka20 ?? 0 },
];

export const getEkKullaniciSayisi = (kullaniciSayisi) =>
  Math.max(0, (Number(kullaniciSayisi) || 0) - 1);

/** Bulut sunucu: taban + (ek kullanıcı sayısı × birim ek kullanıcı fiyatı) */
export const getBulutSunucuToplamFiyat = (fiyatlar, kullaniciSayisi) => {
  if (!fiyatlar) return 0;
  const ekSay = getEkKullaniciSayisi(kullaniciSayisi);
  const taban = Number(fiyatlar.BulutSunucu) || 0;
  const ekBirim = Number(fiyatlar.BulutSunucuEkKullanici) || 0;
  return taban + ekBirim * ekSay;
};

export const getIskontoHaricToplam = (hesaplamaSonucu) => {
  if (!hesaplamaSonucu?.hesaplamaDetaylari) return 0;
  const d = hesaplamaSonucu.hesaplamaDetaylari;
  const paket = d.paketFiyatiOrijinal ?? d.paketFiyati ?? 0;
  const modul =
    (d.modulFiyatlariOrijinal ?? d.modulFiyatlari ?? 0) +
    (d.eFaturaEIrsaliyeFiyati ?? 0);
  const ekKullanici = d.ekKullaniciFiyatiOrijinal ?? d.ekKullaniciFiyati ?? 0;
  return paket + modul + ekKullanici;
};

export const getIskontoSonrasiPaketModul = (hesaplamaSonucu) => {
  if (!hesaplamaSonucu?.hesaplamaDetaylari) return 0;
  const d = hesaplamaSonucu.hesaplamaDetaylari;
  return (
    (d.paketFiyati ?? 0) +
    (d.modulFiyatlari ?? 0) +
    (d.eFaturaEIrsaliyeFiyati ?? 0) +
    (d.ekKullaniciFiyati ?? 0)
  );
};
